import type { EditorTransition, TransitionType } from '@/types/editor';

/** Order used to render the transition picker UI. */
export const TRANSITION_TYPES: TransitionType[] = [
  'fade',
  'dissolve',
  'wipeleft',
  'wiperight',
  'slideleft',
  'slideright',
  'slideup',
  'slidedown',
  'circleopen',
  'radial',
  'smoothleft',
  'smoothright',
];

/** Russian labels for the transition picker UI. */
export const TRANSITION_LABELS: Record<TransitionType, string> = {
  fade: 'Кроссфейд',
  dissolve: 'Растворение',
  wipeleft: 'Шторка ←',
  wiperight: 'Шторка →',
  slideleft: 'Сдвиг ←',
  slideright: 'Сдвиг →',
  slideup: 'Сдвиг ↑',
  slidedown: 'Сдвиг ↓',
  circleopen: 'Круг из центра',
  radial: 'Радиальная шторка',
  smoothleft: 'Мягкая шторка ←',
  smoothright: 'Мягкая шторка →',
};

export const MAX_TRANSITION_SEC = 2;
export const MIN_TRANSITION_SEC = 0.2;

/** Clamp a transition duration (seconds) into the allowed [MIN, MAX] range. */
export function clampTransitionDuration(sec: number): number {
  if (!Number.isFinite(sec)) return MIN_TRANSITION_SEC;
  return Math.min(MAX_TRANSITION_SEC, Math.max(MIN_TRANSITION_SEC, sec));
}

interface TransitionClipLike {
  duration: number;
  transitionIn?: EditorTransition;
}

/**
 * The EFFECTIVE (clamped) duration of the crossfade entering a clip, and the
 * single source of truth every helper below (and the xfade filtergraph
 * builder) must use. A transition can never exceed the length of either clip
 * it straddles — ffmpeg's xfade/acrossfade filters would run past the bounds
 * of whichever input is shorter — so this clamps the transition's
 * MIN/MAX-bounded durationSec down to `min(prevDuration, curDuration)` on top
 * of that. No transition (or nothing to crossfade from) yields 0.
 */
export function effectiveTransitionDuration(
  prevDuration: number,
  curDuration: number,
  transition?: EditorTransition,
): number {
  if (!transition) return 0;
  return Math.min(clampTransitionDuration(transition.durationSec), prevDuration, curDuration);
}

/**
 * Total duration of the rendered timeline once crossfade overlaps are taken
 * into account: the sum of clip durations minus the sum of each transition's
 * EFFECTIVE (clamped) duration (an overlap "steals" time from the naive
 * concatenated length). A transitionIn on clip 0 is meaningless (nothing
 * precedes it to crossfade from), so it is ignored — only clips at index >= 1
 * contribute an overlap.
 */
export function effectiveTimelineDuration(clips: TransitionClipLike[]): number {
  const totalDuration = clips.reduce((sum, clip) => sum + clip.duration, 0);
  let totalEffectiveTransitions = 0;
  for (let k = 1; k < clips.length; k++) {
    totalEffectiveTransitions += effectiveTransitionDuration(
      clips[k - 1].duration,
      clips[k].duration,
      clips[k].transitionIn,
    );
  }
  return totalDuration - totalEffectiveTransitions;
}

/**
 * Absolute start time of each clip on the final overlapped render timeline.
 * clip[0].renderStart = 0; clip[k].renderStart = clip[k-1].renderStart +
 * clip[k-1].duration - effectiveTransitionDuration(...). Because the overlap
 * is clamped to at most clip[k-1].duration, renderStart can never go
 * negative.
 */
export function renderStartTimes(clips: TransitionClipLike[]): number[] {
  const starts: number[] = [];
  for (let k = 0; k < clips.length; k++) {
    if (k === 0) {
      starts.push(0);
      continue;
    }
    const prevStart = starts[k - 1];
    const prevDuration = clips[k - 1].duration;
    const overlap = effectiveTransitionDuration(prevDuration, clips[k].duration, clips[k].transitionIn);
    starts.push(prevStart + prevDuration - overlap);
  }
  return starts;
}

export interface XfadeOffset {
  index: number;
  type: TransitionType;
  durationSec: number;
  offset: number;
}

/**
 * Per-clip ffmpeg xfade offsets: for each clip with a transitionIn, the
 * offset (in seconds) into the accumulated xfade chain where that
 * crossfade begins — equal to the clip's renderStart — paired with the
 * EFFECTIVE (clamped) transition duration used to compute that renderStart.
 * Clips without a transitionIn map to null. The first clip always maps to
 * null, even if it has a transitionIn set, since there is no preceding clip
 * to crossfade from.
 *
 * `durationSec` here is deliberately the clamped value (not the raw
 * transitionIn.durationSec): `offset` is derived from renderStartTimes,
 * which itself accumulates using the clamped duration, so `offset` and
 * `durationSec` always come from the same effective value and can never
 * disagree — this is the single source of truth consumed by the xfade
 * filtergraph builder for both the video xfade and the audio acrossfade.
 *
 * The emitted offset is additionally clamped to be non-negative as a
 * defensive belt-and-braces measure (ffmpeg's xfade filter rejects negative
 * offsets); in practice it can no longer go negative because the effective
 * duration is always <= the preceding clip's duration.
 */
export function xfadeOffsets(clips: TransitionClipLike[]): Array<XfadeOffset | null> {
  const starts = renderStartTimes(clips);
  return clips.map((clip, index) => {
    if (index === 0 || !clip.transitionIn) return null;
    const durationSec = effectiveTransitionDuration(
      clips[index - 1].duration,
      clip.duration,
      clip.transitionIn,
    );
    return {
      index,
      type: clip.transitionIn.type,
      durationSec,
      offset: Math.max(0, starts[index]),
    };
  });
}
